import { LitElement, html } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";

/**
 * <admin-pagination> — Composant de pagination réutilisable pour l'interface admin.
 *
 * Rend des contrôles Bootstrap 5 avec navigation page précédente/suivante,
 * numéros de pages cliquables et ellipses pour les grandes plages.
 *
 * Attributs :
 *   current-page  {number}  Index de la page courante (0-based)
 *   total-pages   {number}  Nombre total de pages
 *   total-items   {number}  Nombre total d'éléments
 *   base-url      {string}  URL de base (sans paramètres)
 *   extra-params  {string}  Paramètres supplémentaires à ajouter à l'URL (ex: "filter=all")
 *
 * Usage Thymeleaf :
 *   <admin-pagination
 *       base-url="/admin/commandes"
 *       th:attr="current-page=${currentPage},total-pages=${totalPages},total-items=${totalItems}">
 *   </admin-pagination>
 */
class AdminPagination extends LitElement {

    /** Utilise le DOM léger pour hériter des styles Bootstrap de la page. */
    createRenderRoot() {
        return this;
    }

    static get properties() {
        return {
            currentPage: { type: Number, attribute: 'current-page' },
            totalPages:  { type: Number, attribute: 'total-pages' },
            totalItems:  { type: Number, attribute: 'total-items' },
            baseUrl:     { type: String, attribute: 'base-url' },
            extraParams: { type: String, attribute: 'extra-params' },
        };
    }

    constructor() {
        super();
        this.currentPage = 0;
        this.totalPages  = 0;
        this.totalItems  = 0;
        this.baseUrl     = '';
        this.extraParams = '';
    }

    _buildUrl(page) {
        let url = this.baseUrl + '?page=' + page;
        if (this.extraParams) {
            url += '&' + this.extraParams;
        }
        return url;
    }

    /**
     * Calcule la liste de pages à afficher avec des marqueurs d'ellipse (-1).
     * Affiche toujours la première, la dernière et les pages autour de la courante.
     */
    _getPageNumbers() {
        const current = this.currentPage;
        const total   = this.totalPages;

        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i);
        }

        const visible = new Set([0, total - 1]);
        for (let i = Math.max(0, current - 2); i <= Math.min(total - 1, current + 2); i++) {
            visible.add(i);
        }

        const sorted = Array.from(visible).sort((a, b) => a - b);
        const result = [];

        for (let i = 0; i < sorted.length; i++) {
            if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
                result.push(-1); // marqueur d'ellipse
            }
            result.push(sorted[i]);
        }

        return result;
    }

    render() {
        if (this.totalPages <= 1) return html``;

        const pages   = this._getPageNumbers();
        const isFirst = this.currentPage === 0;
        const isLast  = this.currentPage === this.totalPages - 1;

        return html`
            <nav aria-label="Navigation pagination" class="mt-4">
                <ul class="pagination justify-content-center flex-wrap">

                    <li class="page-item ${isFirst ? 'disabled' : ''}">
                        <a class="page-link"
                           href="${isFirst ? '#' : this._buildUrl(this.currentPage - 1)}"
                           aria-label="Précédent">
                            <span aria-hidden="true">&laquo;</span>
                        </a>
                    </li>

                    ${pages.map(p => p === -1
                        ? html`<li class="page-item disabled">
                                   <span class="page-link">&hellip;</span>
                               </li>`
                        : html`<li class="page-item ${p === this.currentPage ? 'active' : ''}">
                                   <a class="page-link" href="${this._buildUrl(p)}">${p + 1}</a>
                               </li>`
                    )}

                    <li class="page-item ${isLast ? 'disabled' : ''}">
                        <a class="page-link"
                           href="${isLast ? '#' : this._buildUrl(this.currentPage + 1)}"
                           aria-label="Suivant">
                            <span aria-hidden="true">&raquo;</span>
                        </a>
                    </li>

                </ul>
            </nav>
            <p class="text-center text-secondary mt-2 small">
                Page ${this.currentPage + 1} sur ${this.totalPages}
                &mdash; ${this.totalItems}&nbsp;élément${this.totalItems > 1 ? 's' : ''} au total
            </p>
        `;
    }
}

customElements.define('admin-pagination', AdminPagination);
